import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  CartesianGrid,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"

import { CHART_TOOLTIP_STYLE, HISTORY_LINE_CONFIG } from "components/risk/constants"
import { DuplicateMatchesTable } from "components/risk/DuplicateMatchesTable"
import { RiskActionHistory } from "components/risk/RiskActionHistory"
import { RiskFactorAccordion } from "components/risk/RiskFactorAccordion"
import { RiskScoreHeader } from "components/risk/RiskScoreHeader"

import { useProps } from "contexts/PropsContext"

import { riskService } from "services/risk/risk.service"
import { ActionLog, DuplicateMatch, PlayerRiskHistoryEntry, PlayerRiskProfile, RiskLevelSettings } from "services/risk/types"

import { isAuthorityUnavailableError } from "util/api"

import dayjs from "libs/dayjs"

interface RiskTabProps {
  userId?: string
}

export const RiskTab: FC<RiskTabProps> = ({ userId }) => {
  const { darkMode } = useProps()
  const [riskData, setRiskData] = useState<PlayerRiskProfile | null>(null)
  const [historyData, setHistoryData] = useState<PlayerRiskHistoryEntry[]>([])
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([])
  const [actionLogs, setActionLogs] = useState<ActionLog[]>([])
  const [settings, setSettings] = useState<RiskLevelSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [recalculating, setRecalculating] = useState(false)
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d">("30d")
  const [visibleLines, setVisibleLines] = useState({ total: true, financial: true, profile: true, behavioral: true })

  const toggleLine = (key: keyof typeof visibleLines) => setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }))

  const cardClass =
    "bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl"

  const loadRiskData = useCallback(async () => {
    if (!userId) return
    try {
      const [risk, dupes, logs, levelSettings] = await Promise.all([
        riskService.getPlayerRisk(userId),
        riskService.getPlayerDuplicates(userId),
        riskService.getActionLogs({ page: 1, pageSize: 20 }),
        riskService.getRiskLevelSettings()
      ])
      if (risk) setRiskData(risk)
      if (dupes) setDuplicates(dupes)
      if (levelSettings) setSettings(levelSettings)
      if (logs) {
        const playerLogs = logs.data.filter((l) => l.user_id === userId)
        setActionLogs(playerLogs)
      }
    } finally {
      setLoading(false)
    }
  }, [userId])

  const loadHistory = useCallback(async () => {
    if (!userId) return
    const history = await riskService.getPlayerHistory(userId)
    if (history) setHistoryData(history)
  }, [userId])

  useEffect(() => {
    loadRiskData()
  }, [loadRiskData])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const handleRecalculate = async () => {
    if (!userId) return
    setRecalculating(true)
    try {
      await riskService.recalculatePlayer(userId)
      toast.success("Risk score recalculated")
      await loadRiskData()
      await loadHistory()
    } catch (err: any) {
      // SOFT-567: the score is fail-closed — a 503 means an authority (FS/NUX/bonus-service) is
      // down and the score was NOT recomputed on stale data. Show a retryable message, not a
      // generic error or a false success.
      if (isAuthorityUnavailableError(err)) {
        toast.error("Risk data source temporarily unavailable — please try again in a moment.")
      } else {
        toast.error(err?.data?.message || "Failed to recalculate risk score")
      }
    } finally {
      setRecalculating(false)
    }
  }

  const handleManualAction = async (action: string) => {
    if (!userId) return
    await riskService.triggerManualAction(userId, action, "Manual admin action")
    toast.success(`Action "${action}" executed`)
    await loadRiskData()
  }

  if (loading) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )
  }

  if (!riskData) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <p className="text-gray-500 dark:text-gray-400">No risk score calculated for this player yet.</p>
        <button className="btn btn-primary btn-sm" onClick={handleRecalculate} disabled={recalculating}>
          {recalculating ? <span className="loading loading-spinner loading-xs" /> : null}
          {recalculating ? "Calculating..." : "Calculate Risk Score"}
        </button>
      </div>
    )
  }

  const { risk_score } = riskData

  // Radar chart data
  const radarData = [
    { category: "Financial", score: risk_score.financial_weighted },
    { category: "Profile", score: risk_score.profile_weighted },
    { category: "Behavioral", score: risk_score.behavioral_weighted }
  ]

  // Filter history by time range
  const now = dayjs()
  const rangeDays = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90
  const filteredHistory = historyData.filter((h) => now.diff(dayjs(h.recorded_at), "day") <= rangeDays)

  return (
    <div className="flex flex-col gap-6 pt-6">
      {/* Risk Score Header */}
      <RiskScoreHeader
        riskScore={risk_score}
        onRecalculate={handleRecalculate}
        onManualAction={handleManualAction}
        recalculating={recalculating}
      />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className={`${cardClass} p-6`}>
          <h3 className="text-lg font-semibold mb-4">Category Breakdown</h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData}>
              <PolarGrid stroke={darkMode ? "rgba(255,255,255,0.15)" : "#e5e7eb"} />
              <PolarAngleAxis dataKey="category" tick={{ fill: darkMode ? "#9ca3af" : "#374151", fontSize: 12 }} />
              <Radar dataKey="score" fill="#a855f7" fillOpacity={0.3} stroke="#a855f7" strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* History Line Chart */}
        <div className={`${cardClass} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Risk History</h3>
            <div className="flex gap-1">
              {(["7d", "30d", "90d"] as const).map((range) => (
                <button
                  key={range}
                  className={`btn btn-xs ${timeRange === range ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setTimeRange(range)}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {HISTORY_LINE_CONFIG.map(({ key, label, color }) => (
              <button
                key={key}
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-opacity ${
                  visibleLines[key] ? "opacity-100" : "opacity-40"
                }`}
                onClick={() => toggleLine(key)}
              >
                <span className="w-3 h-0.5 rounded" style={{ backgroundColor: color }} />
                {label}
              </button>
            ))}
          </div>
          {filteredHistory.length === 0 ? (
            <p className="text-sm py-10 text-center text-gray-500 dark:text-gray-400">No history data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={filteredHistory} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "rgba(255,255,255,0.06)" : "#e5e7eb"} />
                <XAxis
                  dataKey="recorded_at"
                  tickFormatter={(v) => dayjs(v).format("DD/MM")}
                  tick={{ fill: darkMode ? "#9ca3af" : "#374151", fontSize: 11 }}
                />
                <YAxis domain={[0, 100]} tick={{ fill: darkMode ? "#9ca3af" : "#374151", fontSize: 11 }} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelFormatter={(v) => dayjs(v).format("DD.MM.YYYY h:mm A")} />
                {visibleLines.total && (
                  <Line type="monotone" dataKey="total_score" stroke="#a855f7" strokeWidth={2} dot={false} name="Total" />
                )}
                {visibleLines.financial && (
                  <Line
                    type="monotone"
                    dataKey="financial_score"
                    stroke="#f43f5e"
                    strokeWidth={1.5}
                    dot={false}
                    name="Financial"
                    strokeDasharray="4 2"
                  />
                )}
                {visibleLines.profile && (
                  <Line
                    type="monotone"
                    dataKey="profile_score"
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    dot={false}
                    name="Profile"
                    strokeDasharray="4 2"
                  />
                )}
                {visibleLines.behavioral && (
                  <Line
                    type="monotone"
                    dataKey="behavioral_score"
                    stroke="#06b6d4"
                    strokeWidth={1.5}
                    dot={false}
                    name="Behavioral"
                    strokeDasharray="4 2"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Factor Details */}
      <div className="flex flex-col gap-3">
        <h3 className="text-lg font-semibold">Risk Factors</h3>
        <RiskFactorAccordion
          categoryName="Financial"
          scores={risk_score.financial_scores}
          weightedScore={risk_score.financial_weighted}
          categoryWeight={settings?.category_weights.financial}
          factorWeights={settings?.factor_weights.financial}
          color="error"
          defaultOpen
        />
        <RiskFactorAccordion
          categoryName="Profile"
          scores={risk_score.profile_scores}
          weightedScore={risk_score.profile_weighted}
          categoryWeight={settings?.category_weights.profile}
          factorWeights={settings?.factor_weights.profile}
          color="warning"
        />
        <RiskFactorAccordion
          categoryName="Behavioral"
          scores={risk_score.behavioral_scores}
          weightedScore={risk_score.behavioral_weighted}
          categoryWeight={settings?.category_weights.behavioral}
          factorWeights={settings?.factor_weights.behavioral}
          color="accent"
        />
      </div>

      {/* Duplicate Matches */}
      {duplicates.length > 0 && <DuplicateMatchesTable duplicates={duplicates} currentUserId={userId!} onReview={loadRiskData} />}

      {/* Action History */}
      {actionLogs.length > 0 && <RiskActionHistory actions={actionLogs} />}
    </div>
  )
}
